/**
 * Collection filters & sorting.
 * Mobile: full-screen drawer from left with Apply/Clear buttons.
 * Desktop: inline sidebar with auto-submit on change.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-filter-form]');
    this.aside = this.querySelector('[data-filters]');
    this.overlay = this.querySelector('[data-filter-overlay]');
    this.label = this.querySelector('[data-filters-label]');
    this.toggleBtn = this.querySelector('[data-toggle-filters]');
    this.labelShow = this.toggleBtn?.dataset.labelShow || 'Show filters';
    this.labelHide = this.toggleBtn?.dataset.labelHide || 'Hide filters';

    this.desktopVisible = true;
    this.mdQuery = window.matchMedia('(min-width: 768px)');

    // Toggle buttons (toolbar + drawer header)
    this.querySelectorAll('[data-toggle-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.toggle())
    );
    this.querySelectorAll('[data-close-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.closeDrawer())
    );
    this.overlay?.addEventListener('click', () => this.closeDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawerOpen) this.closeDrawer();
    });

    // Re-evaluate the toggle label when the viewport crosses the md breakpoint
    // so we never show "Hide filters" while a closed mobile drawer is hidden.
    this.mdQuery.addEventListener('change', () => this.#updateLabel());

    // A plain GET submit sends every field, so an untouched price range and
    // the default sort land in the URL as `filter.v.price.gte=&…&sort_by=manual`.
    // Build the URL ourselves from the fields that actually narrow the list.
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#navigate();
    });

    // Links Shopify builds (chips, "clear all") copy the current query, so a
    // URL that arrived with the noise would keep passing it on.
    const current = new URL(window.location.href);
    const cleaned = this.#cleanParams(current.searchParams);
    if (cleaned.toString() !== current.searchParams.toString()) {
      current.search = cleaned.toString().replace(/\+/g, '%20');
      history.replaceState(history.state, '', current);
    }

    // Colour names are links for crawlers; for shoppers they act as the row's
    // checkbox, so the mobile drawer still waits for Apply.
    this.form?.addEventListener('click', (e) => {
      const link = e.target.closest('[data-filter-link]');
      if (!link) return;
      e.preventDefault();
      const box = link.closest('label')?.querySelector('input[type="checkbox"]');
      if (!box) return;
      box.checked = !box.checked;
      box.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Desktop: auto-submit on change. Mobile: user clicks Apply — except the
    // sort select, which sits in the toolbar outside the drawer and has no
    // Apply button next to it (on phones a new sort did nothing until 2026-10-22).
    this.form?.addEventListener('change', (e) => {
      if (e.target.name === 'sort_by') {
        this.#navigate();
        return;
      }
      if (!this.mdQuery.matches) return;
      if (e.target.type === 'number') {
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => this.#navigate(), 800);
      } else {
        this.#navigate();
      }
    });

    // Price range slider sync
    this.querySelectorAll('[data-range-slider]').forEach((slider) =>
      this.#initRangeSlider(slider)
    );

    // Set initial label
    this.#updateLabel();
  }

  #navigate() {
    const params = this.#cleanParams(new FormData(this.form));
    // URLSearchParams writes spaces as "+", which robots.txt blocks under
    // /collections/*+* — "Svetlo plava" would never reach the index.
    const query = params.toString().replace(/\+/g, '%20');
    window.location.href = query ? `${this.form.action}?${query}` : this.form.action;
  }

  #cleanParams(entries) {
    const defaultSort = this.form?.dataset.defaultSort;
    const params = new URLSearchParams();
    for (const [name, value] of entries) {
      if (value === '') continue;
      if (name === 'sort_by' && value === defaultSort) continue;
      params.append(name, value);
    }
    return params;
  }

  get drawerOpen() {
    return this.aside?.classList.contains('is-open');
  }

  toggle() {
    if (this.mdQuery.matches) {
      this.desktopVisible = !this.desktopVisible;
      this.aside.classList.toggle('is-desktop-hidden');
    } else {
      this.drawerOpen ? this.closeDrawer() : this.openDrawer();
    }
    this.#updateLabel();
  }

  openDrawer() {
    this.aside?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.#updateLabel();
  }

  closeDrawer() {
    this.aside?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    this.#updateLabel();
  }

  #updateLabel() {
    if (!this.label) return;
    // Desktop: reflects the inline sidebar's visible/hidden state.
    // Mobile: reflects the drawer's open/closed state.
    const visible = this.mdQuery.matches ? this.desktopVisible : this.drawerOpen;
    this.label.textContent = visible ? this.labelHide : this.labelShow;
  }

  #initRangeSlider(container) {
    const minThumb = container.querySelector('[data-range-min]');
    const maxThumb = container.querySelector('[data-range-max]');
    const track = container.querySelector('[data-range-track]');
    const wrapper = container.closest('.pb-4') || container.parentElement;
    const minInput = wrapper.querySelector('[data-price-min]');
    const maxInput = wrapper.querySelector('[data-price-max]');
    const minLabel = wrapper.querySelector('[data-label-min]');
    const maxLabel = wrapper.querySelector('[data-label-max]');

    if (!minThumb || !maxThumb || !track) return;

    const rangeMax = parseFloat(container.dataset.rangeMax) || 100;
    const currencySymbol = container.dataset.currency || '$';
    let minVal = parseFloat(minInput?.value) || 0;
    let maxVal = parseFloat(maxInput?.value) || rangeMax;

    const update = () => {
      const minPct = (minVal / rangeMax) * 100;
      const maxPct = (maxVal / rangeMax) * 100;
      minThumb.style.left = `${minPct}%`;
      maxThumb.style.left = `${maxPct}%`;
      track.style.left = `${minPct}%`;
      track.style.right = `${100 - maxPct}%`;
      if (minInput) minInput.value = minVal > 0 ? minVal : '';
      if (maxInput) maxInput.value = maxVal < rangeMax ? maxVal : '';
      if (minLabel) minLabel.textContent = `${currencySymbol}${Math.round(minVal)}`;
      if (maxLabel) maxLabel.textContent = `${currencySymbol}${Math.round(maxVal)}+`;
    };

    let didDrag = false;

    const submitForm = () => {
      // Hidden inputs mutated via JS don't fire `change`, so submit explicitly.
      // Desktop only — mobile users explicitly tap Apply.
      if (!this.mdQuery.matches) return;
      this.#navigate();
    };

    const drag = (thumb, setter) => {
      const onMove = (e) => {
        const rect = container.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        let pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        const val = Math.round((pct / 100) * rangeMax);
        setter(val);
        update();
        didDrag = true;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
        if (didDrag) {
          didDrag = false;
          submitForm();
        }
      };
      thumb.addEventListener('mousedown', (e) => {
        e.preventDefault();
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });
      thumb.addEventListener('touchstart', () => {
        document.addEventListener('touchmove', onMove, { passive: true });
        document.addEventListener('touchend', onUp);
      }, { passive: true });
    };

    drag(minThumb, (v) => { minVal = Math.min(v, maxVal - 1); });
    drag(maxThumb, (v) => { maxVal = Math.max(v, minVal + 1); });

    update();
  }
}

customElements.define('collection-filters', CollectionFilters);

export { CollectionFilters };
